
import React from 'react';
import { UserProfile, ToolRun, MetersSnapshot, PlanType } from '../types';
// PRICING_PLANS is exported from constants.tsx, not types.ts
import { PRICING_PLANS } from '../constants';

interface DashboardProps {
  user: UserProfile;
  recentRuns: ToolRun[];
}

const Dashboard: React.FC<DashboardProps> = ({ user, recentRuns }) => {
  const planConfig = PRICING_PLANS[user.plan];
  
  const meters: MetersSnapshot = {
    credit_balance: user.creditsRemaining,
    credits_reserved: 450, // Simulated reservation
    bot_runs_hour: 0,
    bot_runs_day: 14,
    bot_capacity_total: planConfig.bots * 24
  };

  const stats = [
    { label: 'Available Credits', value: meters.credit_balance.toLocaleString(), icon: '💎', trend: `${meters.credits_reserved} Reserved`, color: 'indigo' },
    { label: 'Bot Capacity', value: `${meters.bot_runs_day} / ${meters.bot_capacity_total}`, icon: '🤖', trend: `${planConfig.bots} Active Bots`, color: 'emerald' },
    { label: 'Storage Used', value: `${user.storageUsedGb} GB / ${planConfig.storageGb} GB`, icon: '💾', trend: '9%', color: 'amber' },
    { label: 'Tool Mastery', value: `${user.activeToolIds.length}`, icon: '🧩', trend: 'Enabled', color: 'purple' },
  ];

  return (
    <div className="space-y-8 max-w-[1600px] mx-auto">
      {/* Meters Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, idx) => (
          <div key={idx} className="bg-white dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 p-6 rounded-3xl shadow-sm hover:shadow-md transition-all">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-2xl shadow-inner">
                {stat.icon}
              </div>
              <span className={`text-[9px] font-black px-2 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 uppercase tracking-widest`}>
                {stat.trend}
              </span>
            </div>
            <div className="text-3xl font-black text-slate-900 dark:text-white mb-1 tracking-tight">{stat.value}</div>
            <div className="text-[10px] text-slate-500 font-black uppercase tracking-widest">{stat.label}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Recent Activity */}
        <div className="lg:col-span-2 space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">System Intelligence Stream</h2>
            <button className="text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:underline uppercase tracking-widest">View Run Logs</button>
          </div>
          <div className="bg-white dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-3xl overflow-hidden shadow-sm">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800">
                  <th className="px-6 py-4 text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">Instance ID</th>
                  <th className="px-6 py-4 text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">Status</th>
                  <th className="px-6 py-4 text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">Cost</th>
                  <th className="px-6 py-4 text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">Time</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800/50">
                {recentRuns.length > 0 ? recentRuns.map((run) => (
                  <tr key={run.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors group">
                    <td className="px-6 py-4 font-bold text-slate-900 dark:text-slate-200 truncate max-w-[200px] uppercase tracking-tighter">ENGINE::{run.toolId}</td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 bg-emerald-100 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-500 text-[9px] font-black rounded uppercase">
                        {run.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-slate-500 text-xs font-bold">{run.creditsSpent} 💎</td>
                    <td className="px-6 py-4 text-slate-400 text-[10px] font-bold uppercase">{new Date(run.timestamp).toLocaleTimeString()}</td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={4} className="px-6 py-20 text-center text-slate-400 italic">No intelligence logs found. ShadowEngine is idle.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Bot Status Panel */}
        <div className="space-y-6">
          <h2 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">Active Bot Fleet</h2>
          <div className="space-y-3">
             {Array.from({ length: planConfig.bots }).map((_, i) => (
               <div key={i} className="bg-white dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 p-4 rounded-2xl flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
                    <div>
                      <div className="text-sm font-black">ShadowBot #{i+1}</div>
                      <div className="text-[10px] text-slate-500 uppercase font-bold tracking-widest">Slot Available</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs font-black text-slate-400">14/24 RUNS</div>
                  </div>
               </div>
             ))}
          </div>

          <div className="p-6 bg-indigo-600 rounded-3xl shadow-xl shadow-indigo-600/20 text-white">
            <h3 className="font-black text-xs uppercase tracking-widest mb-2 opacity-80">Quick Tip</h3>
            <p className="text-sm font-medium leading-relaxed">
              Bot capacity is reserved in 1-hour blocks. If your bots are full, automations will queue for the next available slot.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
